import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { X, GitBranch, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import '../../../component-styles/RecordPages.css';
import API_BASE from '../../../utils/api.js';
import { documentLabel } from '../../../utils/documentTitle.js';

function getApiBase() {
  return API_BASE;
}

// A PO normally traces back to one Invoice. Merged POs cover several, listed
// in `invoiceIds` — the primary `invoiceId` is only the first of them.
function resolveSourceInvoiceIds(purchaseOrder) {
  const primary =
    purchaseOrder.invoiceId ||
    purchaseOrder.sourceInvoiceId ||
    purchaseOrder.source?.invoiceId ||
    purchaseOrder.meta?.invoiceId ||
    '';
  const merged = Array.isArray(purchaseOrder.invoiceIds) ? purchaseOrder.invoiceIds : [];
  const out = [];

  for (const candidate of [primary, ...merged]) {
    const id = String(candidate || '').trim();
    if (id && !out.includes(id)) out.push(id);
  }

  return out;
}

function resolveSourceTbInvoiceId(purchaseOrder) {
  return (
    purchaseOrder.tbInvoiceId ||
    purchaseOrder.sourceTbInvoiceId ||
    purchaseOrder.source?.tbInvoiceId ||
    purchaseOrder.meta?.tbInvoiceId ||
    ''
  );
}

export default function PurchaseOrderFolioModal({ purchaseOrder, projectId, onClose }) {
  const userId = localStorage.getItem('userId');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [sourceType, setSourceType] = useState('invoice');

  useEffect(() => {
    if (!purchaseOrder?.poId) {
      setLoading(false);
      return;
    }

    const sourceInvoiceIds = resolveSourceInvoiceIds(purchaseOrder);
    const sourceTbInvoiceId = resolveSourceTbInvoiceId(purchaseOrder);
    const resolvedSourceType = sourceInvoiceIds.length
      ? 'invoice'
      : sourceTbInvoiceId
        ? 'timebilling'
        : '';
    const sourceIds = sourceInvoiceIds.length ? sourceInvoiceIds : [sourceTbInvoiceId].filter(Boolean);

    if (!userId || !projectId || !sourceIds.length) {
      setInvoices([]);
      setSourceType('invoice');
      setLoading(false);
      return;
    }

    let mounted = true;

    async function load() {
      setLoading(true);
      setError(null);
      setSourceType(resolvedSourceType || 'invoice');

      try {
        const loaded = await Promise.all(
          sourceIds.map(async (sourceId) => {
            const url =
              resolvedSourceType === 'timebilling'
                ? `${getApiBase()}/api/timebilling-invoices/${encodeURIComponent(userId)}/${encodeURIComponent(sourceId)}`
                : `${getApiBase()}/api/invoices/${encodeURIComponent(userId)}/${encodeURIComponent(
                    projectId
                  )}/${encodeURIComponent(sourceId)}`;
            const res = await fetch(url, { cache: 'no-store' });
            const data = res.ok ? await res.json() : null;
            if (!res.ok) throw new Error(data?.error || 'Failed to load source invoice.');
            return data;
          })
        );
        if (mounted) setInvoices(loaded.filter(Boolean));
      } catch (err) {
        if (mounted) {
          setInvoices([]);
          setError(err.message || 'Failed to load source invoice.');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [purchaseOrder, projectId, userId]);

  if (!purchaseOrder) return null;

  const sourceInvoiceIds = resolveSourceInvoiceIds(purchaseOrder);
  const sourceTbInvoiceId = resolveSourceTbInvoiceId(purchaseOrder);
  const hasSource = sourceInvoiceIds.length > 0 || Boolean(sourceTbInvoiceId);
  const sourceLabel = sourceTbInvoiceId ? 'Time Billing Invoice' : 'Invoice';
  const sourcePathFor = (invoice) =>
    sourceTbInvoiceId
      ? `/projects/${projectId}/timebilling-invoices/${sourceTbInvoiceId}`
      : invoice?.invoiceId
        ? `/projects/${projectId}/invoices/${invoice.invoiceId}`
        : '';

  return (
    <div className="folio-overlay" onClick={onClose}>
      <div className="folio-modal" onClick={(e) => e.stopPropagation()}>
        <button className="folio-close" onClick={onClose}>
          <X size={20} />
        </button>

        <h3 className="folio-title">
          {documentLabel(purchaseOrder.orderNumber || purchaseOrder.poId, purchaseOrder.title) ||
            'Purchase Order'}
          <span className="folio-sub">{purchaseOrder.vendorName || '-'}</span>
        </h3>

        <div className="folio-summary">
          <div>
            <label>Status</label>
            <div>{purchaseOrder.status || 'Draft'}</div>
          </div>
          <div>
            <label>Source Type</label>
            <div>{sourceType === 'timebilling' ? 'Time Billing Invoice' : 'Invoice'}</div>
          </div>
        </div>

        {loading && <div className="folio-empty">Loading folio...</div>}
        {!loading && error && <div className="folio-empty">{error}</div>}

        {!loading && !error && (
          <>
            <h4 className="folio-section-label">
              {sourceInvoiceIds.length > 1 ? 'Source Invoices' : 'Source Invoice'}
            </h4>
            <div className="folio-applied-list">
              {!hasSource && (
                <div className="folio-empty">
                  No source invoice is linked to this purchase order.
                </div>
              )}
              {hasSource &&
                invoices.map((invoice) => {
                  const sourcePath = sourcePathFor(invoice);
                  if (!sourcePath) return null;
                  return (
                    <Link
                      key={invoice.invoiceId || invoice.tbInvoiceId}
                      to={sourcePath}
                      className="folio-applied-row"
                    >
                      <GitBranch size={16} className="folio-row-icon" />
                      <div className="folio-row-text">
                        <b>
                          {documentLabel(
                            invoice.invoiceNumber || invoice.tbInvoiceId || invoice.invoiceId,
                            invoice.title
                          )}
                        </b>{' '}
                        -{' '}
                        {sourceLabel}
                      </div>
                      <ExternalLink size={14} className="folio-open-icon" />
                    </Link>
                  );
                })}
              {hasSource && !invoices.length && (
                <div className="folio-empty">The linked invoice could not be loaded.</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

PurchaseOrderFolioModal.propTypes = {
  purchaseOrder: PropTypes.object,
  projectId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};
